import { useMemo, useState } from "react";
import type { Sprint, SprintStatus } from "@/services/sprint.service";

export type SprintStatusFilter = SprintStatus | "all";
export type SprintSortKey = "start_date" | "name" | "status";
export type SprintSortDir = "asc" | "desc";
export interface SprintSort {
	key: SprintSortKey;
	dir: SprintSortDir;
}

const DEFAULT_SORT: SprintSort = { key: "start_date", dir: "desc" };
const STATUS_RANK: Record<SprintStatus, number> = { active: 0, planned: 1, completed: 2 };

function compare(a: Sprint, b: Sprint, key: SprintSortKey) {
	if (key === "status") return STATUS_RANK[a.status] - STATUS_RANK[b.status];
	if (key === "name") return a.name.localeCompare(b.name, undefined, { numeric: true });
	return a.start_date.localeCompare(b.start_date);
}

const PAGE_SIZE = 10;

// ponytail: client-side paging over the full list (1 sprint/week), move to server range() if counts reach thousands
export function useSprintsFilter(sprints: Sprint[]) {
	const [search, setSearchState] = useState("");
	const [status, setStatusState] = useState<SprintStatusFilter>("all");
	const [sort, setSortState] = useState<SprintSort>(DEFAULT_SORT);
	const [rawPage, setPage] = useState(1);

	function setSort(next: SprintSort) {
		setSortState(next);
		setPage(1);
	}

	// Header click: same column flips direction, new column starts at its natural order.
	function toggleSort(key: SprintSortKey) {
		if (sort.key === key) setSort({ key, dir: sort.dir === "asc" ? "desc" : "asc" });
		else setSort({ key, dir: key === "start_date" ? "desc" : "asc" });
	}

	function setSearch(v: string) {
		setSearchState(v);
		setPage(1);
	}

	function setStatus(v: SprintStatusFilter) {
		setStatusState(v);
		setPage(1);
	}

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		const sign = sort.dir === "asc" ? 1 : -1;
		return sprints
			.filter(
				(s) =>
					(status === "all" || s.status === status) &&
					(!q || s.name.toLowerCase().includes(q)),
			)
			.sort((a, b) => sign * compare(a, b, sort.key) || b.start_date.localeCompare(a.start_date));
	}, [sprints, search, status, sort]);

	const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const page = Math.min(rawPage, totalPages);

	const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	return { search, setSearch, status, setStatus, sort, setSort, toggleSort, page, setPage, totalPages, filtered, paged };
}
